import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import api from '../utils/api';
import { getUser, removeUser } from '../utils/auth';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

const FALLBACK_TRIAL = {
  has_access: true,
  is_trial: true,
  trial_days_remaining: 30,
  subscription_status: 'none',
  plan_type: 'founders',
  price_amount: 7.77,
};

export const SubscriptionProvider = ({ children }) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch subscription status from API. Cookie is sent automatically via
  // credentials: 'include'; we no longer read/send a localStorage token.
  const fetchSubscriptionStatus = useCallback(async () => {
    if (!getUser()) {
      setLoading(false);
      setSubscriptionStatus(null);
      return;
    }

    // Hit /auth/me first so the server refreshes the CSRF cookie + Bearer
    // token cache via the response interceptor. 401 clears the user via the
    // axios interceptor; we bail out in that case.
    try {
      await api.get('/auth/me');
    } catch {
      if (!getUser()) {
        removeUser();
        setSubscriptionStatus(null);
        setLoading(false);
        return;
      }
    }

    try {
      const response = await api.get('/subscription/status');
      setSubscriptionStatus(response.data);
      setError(null);
    } catch (err) {
      if (err?.response?.status === 401) {
        // Auth failed completely — let the route guards bounce.
        setSubscriptionStatus(null);
      } else {
        setSubscriptionStatus(FALLBACK_TRIAL);
        setError(err?.message || 'Could not verify subscription');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  // Poll for auth changes (login/logout updates the cached user object).
  useEffect(() => {
    let lastUserId = getUser()?.id || null;

    const interval = setInterval(() => {
      const currentUserId = getUser()?.id || null;

      if (currentUserId !== lastUserId) {
        lastUserId = currentUserId;
        if (currentUserId) {
          setLoading(true);
          fetchSubscriptionStatus();
        } else {
          setSubscriptionStatus(null);
          setLoading(false);
        }
      }
    }, 500);

    return () => clearInterval(interval);
  }, [fetchSubscriptionStatus]);

  const hasAccess = subscriptionStatus?.has_access ?? true;
  const isTrialActive = subscriptionStatus?.is_trial ?? false;
  const trialDaysRemaining = subscriptionStatus?.trial_days_remaining ?? 0;
  const isSubscribed = subscriptionStatus?.subscription_status === 'active';

  const refreshSubscription = useCallback(() => {
    setLoading(true);
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  return (
    <SubscriptionContext.Provider value={{
      subscriptionStatus,
      loading,
      error,
      hasAccess,
      isTrialActive,
      trialDaysRemaining,
      isSubscribed,
      refreshSubscription,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
